<script>
	import Radio from "./ui/Radio.svelte";
	import Checkbox from "./ui/Checkbox.svelte";
	import Button from "./ui/Button.svelte";
	import Counter from "./ui/Counter.svelte";

	import { licenses, supports, productsList, buyLink, buyPrice } from "./data";

	export let coupon;
	export let product;
	export let params;

	const license = licenses.find(a => a.id === params.name);

	const Logo = license.logo;

	let support = license.support;
	let devs = license.devs;

	const sOptions = license.supportOptions.map((a, i) => ({
		id: supports[a].id,
		name: supports[a].name + (i == 0 ? "" : ` ($${supports[a].price})`),
	}));

	let widgets = {};
	if (license.widgets == Infinity) {
		productsList.forEach(a => (widgets[a.id] = true));
	}
	if (product) {
		widgets[product] = true;
		if (!license.widgets) {
			const obj = productsList.find(a => a.id == product);
			license = { ...license };
			license._price -= obj.price;
		}
	}

	let hasWidgets;
	$: hasWidgets = Object.keys(widgets).length;
	let pOptions;
	$: {
		pOptions = productsList.map(a => {
			let name;
			if (hasWidgets < license.widgets) {
				name = `${a.name} `;
			} else {
				name = `${a.name} ($${a.price})`;
			}

			return {
				id: a.id,
				price: a.price,
				base: a.name,
				name,
			};
		});
		if (license.widgets && license.widgets != Infinity) {
			const max = Math.min(hasWidgets, license.widgets);
			[]
				.concat(pOptions)
				.filter(a => widgets[a.id])
				.sort((a, b) => (a.price > b.price ? -1 : 1))
				.slice(0, max)
				.forEach(a => (a.name = `${a.base} (free of charge)`));
		}
	}

	let price;
	$: {
		price = buyPrice(license, support, devs, widgets, coupon);
	}

	function num2str(value, word) {
		if (value === 1) {
			return `One ${word}`;
		} else if (value === 2) {
			return `Two ${word}s`;
		} else if (value === Infinity) {
			return `Unlimited number of ${word}s`;
		} else {
			return `${value} ${word}s`;
		}
	}
</script>

<div class="container">

	<div class="main">
		<div class="icon">
			<div class="pricing-icon">
				<Logo />
			</div>
		</div>
		<div class="description">
			<div class="block">
				<h2>{license.name}</h2>
				<ul class="info">
					<li>{num2str(license.projects, 'project')}</li>
					<li>{num2str(license.devs, 'developer')}</li>
					{#if license.sas}
						<li>Can be used in SaaS products</li>
					{/if}
					<li>One year of updates and support</li>
					<li>Webix UI Library</li>
					<li>{license.package}</li>
					<li>
						<a href="/legalnote/developer-license-agreement/" target="_blank">
							Perpetual Commercial License
						</a>
					</li>
				</ul>
			</div>
			<div class="block">
				{#if support == 'basic'}
					<div>
						<h3>Basic Support:</h3>
						<ul>
							<li>Up to 72 hours initial response time</li>
							<li>10 tickets</li>
						</ul>
					</div>
				{:else if support == 'standard'}
					<div>
						<h3>Standard support:</h3>
						<ul>
							<li>Up to 48 hours initial response time</li>
							<li>30 tickets / Unlimited 1st month</li>
						</ul>
					</div>
				{:else}
					<div>
						<h3>Priority support:</h3>
						<ul>
							<li>Up to 24 hours initial response time</li>
							<li>Unlimited tickets</li>
							<li>10 live chat sessions</li>
							<li>Emergency bug fixes</li>
							<li>Integration audit</li>
						</ul>
					</div>
				{/if}
			</div>

			{#if hasWidgets}
				<div class="block">
					<h3>Complex Widgets:</h3>
					<ul>
						{#each productsList as prod (prod.id)}
							{#if widgets[prod.id]}
								<li>{prod.name}</li>
							{/if}
						{/each}
					</ul>
				</div>
			{/if}
		</div>
	</div>

	<div class="sidebar">
		{#if license.extraDevPrice}
			<div class="block">
				<h3>Developers:</h3>
				<Counter
					bind:value={devs}
					min={license._devs}
					max={license.maxDevs}
					name="developer" />
			</div>
		{/if}

		{#if license.supportOptions}
			<div class="block">
				<h3>Support Plan:</h3>
				<ul>
					<Radio options={sOptions} bind:value={support} />
				</ul>
			</div>
		{/if}

		{#if license.widgets < Infinity}
			<div class="block">
				<h3>Complex Widgets:</h3>
				<ul>
					<Checkbox options={pOptions} bind:value={widgets} />
				</ul>
			</div>
		{/if}

		<div class="total">
			<div class="row">
				<span class="title">Total:</span>
				<span class="price">${price}</span>
			</div>
			<div class="row">
				<span class="title">Coupon:</span>
				<input
					type="text"
					class="input"
					placeholder="Enter the coupon code"
					bind:value={coupon} />
			</div>
			<Button
				href={buyLink(license, support, devs, widgets, coupon)}
				label="Add to Cart" />
		</div>
	</div>
</div>

<style>
	.container {
		max-width: 111rem;
		padding: 2.5rem;
		margin: 0 auto;
		background-color: transparent;
		animation: fadeIn 0.4s 1 ease-in-out;
		animation-fill-mode: forwards;
		border: 1px solid #dedede;
		font-family: Roboto;
		font-style: normal;
		font-size: 16px;
		line-height: 25px;
		color: rgba(0, 0, 0, 0.7);
		box-sizing: border-box;

		display: flex;
	}
	.main {
		flex: 2;
		margin-top: 2rem;
	}
	.description {
		margin-left: 33.5%;
		padding-bottom: 2rem;
	}

	ul {
		font-size: 1.6rem;
		line-height: 3rem;
		color: rgba(0, 0, 0, 0.6);
	}

	.icon {
		float: left;
		width: 31.5%;
		min-width: 8rem;
		text-align: center;
	}
	.pricing-icon {
		display: inline-block;
		vertical-align: top;
		width: 65px;
	}

	h2 {
		font-size: 2rem;
		line-height: 3rem;
		font-weight: 500;
		text-transform: uppercase;
		margin-bottom: 1.5rem;
	}

	h3 {
		font-size: 1.6rem;
		line-height: 2.5rem;
		font-weight: 500;
		margin-bottom: 1rem;
	}

	.sidebar {
		flex: 1;

		margin-left: 1rem;
		padding: 2.5rem;
		background-color: #fff;
	}

	.block {
		margin-top: 3.3rem;
	}

	.block:first-child {
		margin-top: 0;
	}
	.total {
		margin-top: 2.3rem;
		border-top: 1px solid #ebebeb;
		padding-top: 1.5rem;
	}
	.title {
		font-size: 2rem;
		line-height: 3rem;
		color: rgba(0, 0, 0, 0.6);
		text-transform: uppercase;
		margin-right: 0.7rem;
		width: 84px;
	}
	.row {
		margin-top: 1.5rem;
		display: flex;
		align-items: center;
	}
	.price {
		font-size: 2rem;
		line-height: 3rem;
		text-transform: uppercase;
		color: rgba(0, 0, 0, 0.7);
		font-weight: 500;
	}
	.input {
		border: 1px solid #dedede;
		box-sizing: border-box;
		border-radius: 2px;
		padding: 7px 8px;
		flex-grow: 1;
		flex-shrink: 1;
		width: 100%;
		font-family: Roboto;
		font-style: normal;
		font-weight: normal;
		font-size: 14px;
		line-height: 16px;
	}
	@media screen and (max-width: 820px) {
		.container {
			flex-wrap: wrap;
			max-width: 52rem;
		}
		.main,
		.sidebar {
			flex: initial;
			width: 100%;
		}
		.sidebar {
			padding: 0;
			margin: 0;
			background: transparent;
		}
		.icon {
			float: none;
			margin: 0 auto 2rem;
		}
		.description {
			margin-left: 0;
		}
		h2 {
			text-align: center;
			margin-bottom: 3.3rem;
		}
	}
</style>
